import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Image as ImageIcon, Upload, Loader2, Trash2, MapPin, ChevronLeft, ChevronRight, X as XIcon, Download } from 'lucide-react';

const ACCEPT = 'image/jpeg,image/jpg,image/png,image/webp,image/gif,image/bmp';
const MAX_BYTES = 10 * 1024 * 1024;
const MAX_IMAGES = 20;

/**
 * Image gallery attached to an SMB lead. Used by data-collector agents (which
 * push Google Maps photos via POST /smb/leads/{id}/images with
 * source=google_maps) and by humans who need to upload supporting pictures
 * from the lead detail page.
 */
export const LeadImagesGallery = ({ leadId }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  // Index into `images` for the lightbox; null = closed.
  const [previewIndex, setPreviewIndex] = useState(null);
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const apiBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  const fetchImages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/smb/leads/${leadId}/images`);
      if (res.data?.success) setImages(res.data.data || []);
    } catch {
      // empty gallery is the default on error — no toast here
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { fetchImages(); }, [fetchImages]);

  const validateAndUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const tooBig = files.find(f => f.size > MAX_BYTES);
    if (tooBig) {
      toast.error(`"${tooBig.name}" exceeds the 10 MB limit`);
      return;
    }
    const slots = MAX_IMAGES - images.length;
    if (files.length > slots) {
      toast.error(`Can only add ${slots} more image(s) — limit is ${MAX_IMAGES} per lead`);
      return;
    }

    const form = new FormData();
    files.forEach(f => form.append('files', f));
    form.append('source', 'manual_upload');

    setUploading(true);
    try {
      const res = await api.post(`/smb/leads/${leadId}/images`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.success) {
        setImages(res.data.data || []);
        toast.success(`${files.length} image${files.length === 1 ? '' : 's'} uploaded`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleFilePick = (e) => {
    validateAndUpload(e.target.files);
    e.target.value = '';  // reset so the same file can be selected again later
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    validateAndUpload(e.dataTransfer.files);
  };

  const handleDelete = async (imageId, originalName) => {
    if (!window.confirm(`Delete "${originalName}"?`)) return;
    try {
      const res = await api.delete(`/smb/leads/${leadId}/images/${imageId}`);
      if (res.data?.success) {
        setImages(res.data.data || []);
        toast.success('Image removed');
      }
    } catch {
      toast.error('Could not delete image');
    }
  };

  const fullUrl = (url) => (url && url.startsWith('http') ? url : `${apiBase}${url}`);

  const closePreview = useCallback(() => setPreviewIndex(null), []);
  const showPrev = useCallback(() => {
    setPreviewIndex(i => (i == null ? null : (i - 1 + images.length) % images.length));
  }, [images.length]);
  const showNext = useCallback(() => {
    setPreviewIndex(i => (i == null ? null : (i + 1) % images.length));
  }, [images.length]);

  // Keyboard nav: ESC closes, ←/→ navigate.
  useEffect(() => {
    if (previewIndex == null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closePreview();
      else if (e.key === 'ArrowLeft') showPrev();
      else if (e.key === 'ArrowRight') showNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [previewIndex, closePreview, showPrev, showNext]);

  // Force-download via a Blob fetch so the browser saves the file under its
  // original name regardless of cross-origin headers (an `<a download>` won't
  // work for cross-origin without explicit Content-Disposition).
  const downloadImage = async (img) => {
    try {
      const resp = await fetch(fullUrl(img.url), { credentials: 'include' });
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = img.original_name || `image-${img.id}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      toast.error('Download failed');
    }
  };

  return (
    <Card data-testid="lead-images-gallery">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <ImageIcon className="h-4 w-4 text-muted-foreground" />
            Business Images
            {images.length > 0 && (
              <Badge variant="outline" className="ml-1">{images.length} / {MAX_IMAGES}</Badge>
            )}
          </CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || images.length >= MAX_IMAGES}
            data-testid="lead-images-upload-btn"
          >
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
            Upload
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPT}
            className="hidden"
            onChange={handleFilePick}
            data-testid="lead-images-file-input"
          />
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : images.length === 0 ? (
          <div
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            className={`rounded-lg border-2 border-dashed py-10 text-center text-sm cursor-pointer transition-colors ${
              dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/30 hover:border-muted-foreground/60'
            }`}
            onClick={() => fileInputRef.current?.click()}
            data-testid="lead-images-empty-state"
          >
            <ImageIcon className="h-8 w-8 mx-auto mb-2 text-muted-foreground/60" />
            <p className="font-medium">No images yet</p>
            <p className="text-muted-foreground text-xs mt-1">
              Drop files here or click to upload (max {MAX_IMAGES}, 10 MB each)
            </p>
          </div>
        ) : (
          <div
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            className={`grid grid-cols-2 sm:grid-cols-3 gap-2 ${dragOver ? 'ring-2 ring-primary rounded-lg p-1' : ''}`}
          >
            {images.map((img) => (
              <div
                key={img.id}
                className="relative group rounded-lg overflow-hidden bg-muted aspect-square cursor-zoom-in"
                onClick={() => setPreviewIndex(images.findIndex(i => i.id === img.id))}
                data-testid={`lead-image-${img.id}`}
              >
                <img
                  src={fullUrl(img.url)}
                  alt={img.original_name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  loading="lazy"
                />
                {img.source === 'google_maps' && (
                  <Badge className="absolute top-1 left-1 bg-blue-600/90 text-white text-[9px] px-1.5 py-0 h-4 gap-0.5">
                    <MapPin className="h-2.5 w-2.5" />Maps
                  </Badge>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); downloadImage(img); }}
                  className="absolute bottom-1 right-1 bg-black/70 hover:bg-black/85 text-white rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`lead-image-download-${img.id}`}
                  title="Download"
                >
                  <Download className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleDelete(img.id, img.original_name); }}
                  className="absolute top-1 right-1 bg-red-600/90 hover:bg-red-600 text-white rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`lead-image-delete-${img.id}`}
                  title="Remove image"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {previewIndex != null && images[previewIndex] && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex flex-col"
          onClick={closePreview}
          data-testid="lead-image-lightbox"
        >
          {/* Top bar: filename + actions */}
          <div
            className="flex items-center justify-between px-5 py-3 text-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-sm font-medium truncate" data-testid="lightbox-filename">
                {images[previewIndex].original_name}
              </span>
              <span className="text-xs text-white/60 shrink-0">
                {previewIndex + 1} of {images.length}
              </span>
              {images[previewIndex].source === 'google_maps' && (
                <Badge className="bg-blue-600/90 text-white text-[10px] px-1.5 py-0 h-5 gap-0.5 shrink-0">
                  <MapPin className="h-3 w-3" />Maps
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => downloadImage(images[previewIndex])}
                data-testid="lightbox-download-btn"
                className="bg-white/10 hover:bg-white/20 text-white border-0"
              >
                <Download className="h-3.5 w-3.5 mr-1" />
                Download
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={closePreview}
                data-testid="lightbox-close-btn"
                className="bg-white/10 hover:bg-white/20 text-white border-0"
                aria-label="Close"
              >
                <XIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Stage: prev arrow, image, next arrow */}
          <div className="flex-1 flex items-center justify-center px-4 overflow-hidden">
            {images.length > 1 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); showPrev(); }}
                aria-label="Previous image"
                data-testid="lightbox-prev-btn"
                className="shrink-0 rounded-full bg-white/10 hover:bg-white/20 text-white p-2 transition-colors"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
            )}
            <img
              src={fullUrl(images[previewIndex].url)}
              alt={images[previewIndex].original_name}
              className="max-h-[80vh] max-w-[80vw] object-contain mx-4 rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="lightbox-image"
            />
            {images.length > 1 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); showNext(); }}
                aria-label="Next image"
                data-testid="lightbox-next-btn"
                className="shrink-0 rounded-full bg-white/10 hover:bg-white/20 text-white p-2 transition-colors"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            )}
          </div>

          {/* Bottom strip: clickable thumbnails when more than one image */}
          {images.length > 1 && (
            <div
              className="flex gap-2 justify-center px-4 py-3 overflow-x-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {images.map((img, idx) => (
                <button
                  key={img.id}
                  type="button"
                  onClick={() => setPreviewIndex(idx)}
                  className={`shrink-0 rounded overflow-hidden transition-all ${
                    idx === previewIndex ? 'ring-2 ring-primary opacity-100' : 'opacity-50 hover:opacity-80'
                  }`}
                  data-testid={`lightbox-thumb-${img.id}`}
                  aria-label={`Show image ${idx + 1}`}
                >
                  <img
                    src={fullUrl(img.url)}
                    alt=""
                    className="h-14 w-14 object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default LeadImagesGallery;
